/**
 * @name useAccountList.js
 * @author 양진영
 * @since 2025. 02. 28.
 * @description
 * 계정 관리 로직을 담당하는 React 훅
 * 
 * @param {string} compCode - 관리자 : 선택된 사업장 코드 (전역), 유저 : 로그인 정보에 담긴 사업장 코드 
 * @returns {Object} - 함수와 상태를 반환
 * */

import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { get, post } from "@utils/axios_api";
import { toast } from 'react-toastify';

const fetchAccountList = async (params) => {
  const response = await get("/admin/comm/account/accountList", { params });

  return response;
};

export function useAccountList(compCode, compName) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedItems, setSelectedItems] = useState([]);

  const methods = useForm({
    defaultValues: {
    },
    shouldFocusError: false, // Focus 방지(포커스 구현시 React ref 구현 해야함)
  });

  const initialAccount = {
    compCode: compCode,
    compName: compName,
    id: '',
    password: '',
    name: null,
    phoneNo: null,
    email: null,
    roleId: 'R2',
    useYn: 'Y',
    isNew: true,
    isEdited: false,
  };

  const { data: roleList = [] } = useQuery({
    queryKey: ['roleList'],
    queryFn: () => get('/admin/comm/role/list').then(res => res?.result ?? []),
    staleTime: 5 * 60 * 1000,
  });

  const roleOptions = roleList.map(role => ({
    key: role.roleCode,
    name: role.displayName || role.roleName,
  }));

  const useYnOptions = [
    { key: "Y", name: "Y" },
    { key: "N", name: "N" },
  ];

  const { watch, setValue } = methods;

  const pageIndex = parseInt(searchParams.get("pageIndex") || "1", 10);
  const recordCountPerPage = parseInt(searchParams.get("pageSize") || "10", 10);

  const queryParam = {
    pageIndex,
    recordCountPerPage,
    compCode,
  };

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["accountList", queryParam],
    queryFn: () => fetchAccountList(queryParam),
    keepPreviousData: true,
    staleTime: 600000,
  });

  const refreshList = () => {
    refetch();
  };

  const onSubmit = (values) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("pageIndex", "1");
    setSearchParams(newParams);
  };

  const handlePageChange = (targetPage) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("pageIndex", targetPage);
    setSearchParams(newParams);
  };

  const changePageSize = (size) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("pageSize", String(size));
    newParams.set("pageIndex", "1");
    setSearchParams(newParams);
  };

  const handleSelectAll = (e) => {
    if (!data || !data.resultList) return;
    const { checked } = e.target;
    if (checked) {
      const allIds = data.resultList.map((item) => item.id);
      setSelectedItems(allIds);
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (id) => {
    if (selectedItems.includes(id)) {
      setSelectedItems(selectedItems.filter((item) => item !== id));
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  };

  const selectedDelete = async () => {
    try {

      const payload = selectedItems.map(id => ({ id }));

      const response = await post('/admin/comm/account/delete', payload, {
        headers: { 'Content-type': 'application/json' }
      })

      toast.success(response.message);
      await refetch();

    } catch (error) {
      console.log("삭제 중 오류:", error);
    }
  }

  const handleDelete = (e) => {
    e.preventDefault();

    if (selectedItems.length === 0) {
      toast.warn("삭제할 항목을 선택해주세요.");
      return;
    }

    if (!window.confirm("삭제 하시겠습니까?")) return;

    selectedDelete();

  };

  // 체크 여부
  const isAllChecked = !!(data?.resultList && data.resultList.length > 0 && selectedItems.length === data.resultList.length);

  return {
    methods,
    data,
    isLoading,
    isError,
    error,
    onSubmit,
    handlePageChange,
    changePageSize,
    handleDelete,
    selectedDelete,
    selectedItems,
    handleSelectItem,
    handleSelectAll,
    pageIndex,
    recordCountPerPage,
    refetch,
    refreshList,
    watch,
    setValue,
    initialAccount,
    roleOptions,
    useYnOptions,
    isAllChecked,
  };
}
